/**
 * @format
 */

import { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchUserData, setLoading, setError } from 'state/slice/userSlice';
import { RootState } from 'state/store';
import { authService } from '../services/authService';
import {
  handleTokenStorage,
  handlePasswordChangeSession,
  getNavigationPath,
} from '../utils/authUtils';
import { SignInCredentials, SignInResponse } from '../types/auth.types';

export const useAuth = () => {
  const [localError, setLocalError] = useState<string>('');
  const [isNavigating, setIsNavigating] = useState(false);
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const userData = useSelector((state: RootState) => (state.user as any).userData);

  // Navigate after user data is fetched
  useEffect(() => {
    if (isNavigating && userData?.role) {
      const navigationPath = getNavigationPath(userData.role);
      navigate(navigationPath);
      setIsNavigating(false);
    }
  }, [userData, isNavigating, navigate]);

  const signIn = useCallback(
    async (credentials: SignInCredentials) => {
      dispatch(setLoading(true));
      setLocalError('');

      try {
        const signInResponse: SignInResponse = await authService.signIn(
          credentials
        );

        if (signInResponse.challengeName === 'NEW_PASSWORD_REQUIRED') {
          handlePasswordChangeSession(signInResponse, credentials.username);
          navigate('/update-password');
        } else {
          handleTokenStorage(signInResponse);
          dispatch(fetchUserData() as any);
          setIsNavigating(true);
        }

        return signInResponse;
      } catch (error: any) {
        const errorMessage =
          error.message ||
          'Error signing in. Please check your credentials and try again.';

        setLocalError(errorMessage);
        dispatch(setError(errorMessage));
        throw error;
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch, navigate]
  );

  const clearError = useCallback(() => {
    setLocalError('');
  }, []);

  return {
    signIn,
    error: localError,
    clearError,
  };
};
